import { useCallback, useState } from 'react';
import { useSimuladorEnergiaSolarState } from '../state';
import { getApiUrl, handleFetchResponse } from '../../shared/utils/api';
import { useLoading } from '../../../../utils/loading';
import { devLog } from '../../../../utils/browser';

export function useStoreActions() {
  const { token, setStores } = useSimuladorEnergiaSolarState();
  const [lastStoreData, setLastStoreData] = useState([]);
  const [lastFetchedCep, setLastFetchedCep] = useState();

  const [lastGeocodeData, setLastGeocodeData] = useState();
  const [lastCepGeocode, setLastCepGeocode] = useState();

  const [lastFecthedQuery, setLastFecthedQuery] = useState('');
  const [lastStoreQueryData, setLastStoreQueryData] = useState([]);

  const loadingState = useLoading();

  const findStoresByCep = useCallback(
    async (params) => {
      if (loadingState.loadingMap['find-stores-by-cep']) return;

      if (lastFetchedCep === params.postalCode && !!lastFetchedCep && !!params.postalCode) {
        devLog('[find-stores-by-cep] Returning cached data for CEP:', params.postalCode);
        return lastStoreData;
      }

      loadingState.setLoading('find-stores-by-cep', true);

      try {
        const data = await handleFetchResponse(
          fetch(getApiUrl(`energia-solar/v2/find-stores`), {
            method: 'POST',
            body: JSON.stringify(params),
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          }),
        );

        setLastFetchedCep(params.postalCode);
        setLastStoreData(data);
        setStores(data);

        return data;
      } catch (error) {
        setLastFetchedCep(null);
        setLastStoreData([]);
        throw error;
      } finally {
        loadingState.setLoading('find-stores-by-cep', false);
      }
    },
    [lastFetchedCep, lastStoreData, loadingState, setStores, token],
  );

  const findStoresByText = useCallback(
    async (q = '') => {
      if (loadingState.loadingMap['find-stores-by-text']) return;

      if (lastFecthedQuery === q && !!q && !!lastFecthedQuery) {
        devLog('[find-stores-by-text] Returning cached data for query:', q);
        return lastStoreQueryData;
      }

      loadingState.setLoading('find-stores-by-text', true);

      try {
        const data = await handleFetchResponse(
          fetch(getApiUrl(`energia-solar/v2/find-stores-by-text?q=${encodeURIComponent(q)}`), {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          }),
        );

        setLastFecthedQuery(q);
        setLastStoreQueryData(data);
        setStores(data);

        return data;
      } catch (error) {
        setLastFecthedQuery('');
        setLastStoreQueryData([]);
        return [];
      } finally {
        loadingState.setLoading('find-stores-by-text', false);
      }
    },
    [lastFecthedQuery, lastStoreQueryData, loadingState, setStores, token],
  );

  const findCepGeocode = useCallback(
    async (cep) => {
      if (loadingState.loadingMap['find-cep-geocode']) return;

      if (lastCepGeocode === cep && !!lastCepGeocode && !!cep) {
        devLog('[find-cep-geocode] Returning cached geocode data for CEP:', cep);
        return lastGeocodeData;
      }

      loadingState.setLoading('find-cep-geocode', true);

      try {
        const data = await handleFetchResponse(
          fetch(getApiUrl(`energia-solar/v2/cep-geocode/${cep.replace(/\D/g, '')}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        );

        setLastCepGeocode(cep);
        setLastGeocodeData(data);

        return data;
      } catch (error) {
        setLastCepGeocode(null);
        setLastGeocodeData(null);
        devLog('[find-cep-geocode] Error:', error);
        return null;
      } finally {
        loadingState.setLoading('find-cep-geocode', false);
      }
    },
    [lastCepGeocode, lastGeocodeData, loadingState, token],
  );

  return {
    findStoresByCep,
    findCepGeocode,
    findStoresByText,
    loadingStores:
      loadingState.loadingMap['find-stores-by-cep'] ||
      loadingState.loadingMap['find-cep-geocode'] ||
      loadingState.loadingMap['find-stores-by-text'] ||
      false,
  };
}
